/**
 * React hook for browser-side AI inference in Helios.
 *
 * Provides NER, embeddings, sentiment analysis, and document classification
 * that run entirely in the browser using ONNX Runtime Web.
 * Models are auto-downloaded and cached on first use.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserAIEngine } from '../gpu/onnx-runtime';
import type { NEREntity, ClassificationResult, SentimentResult, ModelInfo } from '../gpu/onnx-runtime';

export interface UseBrowserAIResult {
  /** Run Named Entity Recognition on text */
  runNER: (text: string) => Promise<NEREntity[]>;
  /** Compute text embeddings */
  computeEmbeddings: (texts: string[]) => Promise<Float32Array[]>;
  /** Analyze sentiment of text */
  analyzeSentiment: (text: string) => Promise<SentimentResult>;
  /** Classify a document */
  classifyDocument: (text: string) => Promise<ClassificationResult[]>;
  /** Load a specific model (triggers download if not cached) */
  loadModel: (modelId: string) => Promise<void>;
  /** Whether the AI engine is initialized */
  isInitialized: boolean;
  /** Whether a specific model is loaded */
  isModelLoaded: (modelId: string) => boolean;
  /** Current model loading progress (0-1) */
  loadingProgress: number;
  /** Name of the model currently being loaded */
  loadingModel: string | null;
  /** Whether the engine uses GPU acceleration */
  isGPUAccelerated: boolean;
  /** List of available models */
  availableModels: ModelInfo[];
  /** Error if initialization failed */
  error: Error | null;
}

// Singleton engine shared across hook instances
let sharedEngine: BrowserAIEngine | null = null;
let initPromise: Promise<void> | null = null;

export function useBrowserAI(): UseBrowserAIResult {
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingModel, setLoadingModel] = useState<string | null>(null);
  const [isGPUAccelerated, setIsGPUAccelerated] = useState(false);
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const engineRef = useRef<BrowserAIEngine | null>(null);

  useEffect(() => {
    const initEngine = async () => {
      try {
        if (!sharedEngine) {
          sharedEngine = new BrowserAIEngine();
          initPromise = sharedEngine.init();
        }

        if (initPromise) {
          await initPromise;
        }

        engineRef.current = sharedEngine;
        setIsInitialized(true);
        setIsGPUAccelerated(sharedEngine.isGPUAccelerated());
        setAvailableModels(sharedEngine.getAvailableModels());
      } catch (err) {
        console.error('[useBrowserAI] Initialization failed:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    };

    initEngine();
  }, []);

  const loadModel = useCallback(
    async (modelId: string) => {
      const engine = engineRef.current;
      if (!engine) throw new Error('BrowserAI not initialized');

      if (engine.isModelLoaded(modelId)) return;

      setLoadingModel(modelId);
      setLoadingProgress(0);

      try {
        await engine.loadModel(modelId, (loaded, total) => {
          setLoadingProgress(total > 0 ? loaded / total : 0);
        });
      } finally {
        setLoadingModel(null);
        setLoadingProgress(0);
      }
    },
    []
  );

  const runNER = useCallback(
    async (text: string): Promise<NEREntity[]> => {
      const engine = engineRef.current;
      if (!engine) {
        console.warn('[useBrowserAI] Engine not ready, falling back to API');
        return [];
      }
      return engine.runNER(text);
    },
    []
  );

  const computeEmbeddings = useCallback(
    async (texts: string[]): Promise<Float32Array[]> => {
      const engine = engineRef.current;
      if (!engine) {
        return texts.map(() => new Float32Array(384));
      }
      return engine.computeEmbeddings(texts);
    },
    []
  );

  const analyzeSentiment = useCallback(
    async (text: string): Promise<SentimentResult> => {
      const engine = engineRef.current;
      if (!engine) {
        return {
          label: 'neutral',
          score: 0.5,
          scores: { positive: 0.33, negative: 0.33, neutral: 0.34 },
        };
      }
      return engine.analyzeSentiment(text);
    },
    []
  );

  const classifyDocument = useCallback(
    async (text: string): Promise<ClassificationResult[]> => {
      const engine = engineRef.current;
      if (!engine) return [];
      return engine.classifyDocument(text);
    },
    []
  );

  const isModelLoaded = useCallback(
    (modelId: string): boolean => {
      return engineRef.current?.isModelLoaded(modelId) ?? false;
    },
    []
  );

  return {
    runNER,
    computeEmbeddings,
    analyzeSentiment,
    classifyDocument,
    loadModel,
    isInitialized,
    isModelLoaded,
    loadingProgress,
    loadingModel,
    isGPUAccelerated,
    availableModels,
    error,
  };
}

export default useBrowserAI;
